import { useState } from "react";
import { Check, Copy, Eye, EyeOff } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { copyToClipboard } from "@/lib/clipboard";
import { cn } from "@/lib/cn";
import { maskSecret } from "@/lib/format";
import { strings } from "@/lib/strings";
import type { WizardProvisionedCredential } from "@/types/wizard";
import { toast } from "sonner";

interface CredentialTableProps {
  credentials: WizardProvisionedCredential[];
  showSecret?: boolean;
  revealAll?: boolean;
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="ghost"
      size="icon"
      className="size-7"
      disabled={!value}
      aria-label={`Copy ${label}`}
      onClick={async () => {
        if (await copyToClipboard(value)) {
          setCopied(true);
          toast.success(`${label} copied.`);
          setTimeout(() => setCopied(false), 1200);
        } else {
          toast.error(strings.toast.copyFailed);
        }
      }}
    >
      {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
    </Button>
  );
}

export function CredentialTable({ credentials, showSecret = false, revealAll = false }: CredentialTableProps) {
  const [revealedKeys, setRevealedKeys] = useState<Set<string>>(new Set());

  if (!credentials.length) {
    return (
      <div className="rounded-lg border border-dashed border-border/60 px-4 py-8 text-center text-sm text-muted-foreground">
        {strings.pages.review.noApplications}
      </div>
    );
  }

  function isRevealed(key: string) {
    return revealAll || revealedKeys.has(key);
  }

  function toggleReveal(key: string) {
    setRevealedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <div className="rounded-lg border border-border/60">
      <table className="data-table w-full text-left text-sm">
        <thead>
          <tr className="border-b border-border/60 bg-muted/30 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            <th className="px-3 py-2 font-mono">{strings.table.key}</th>
            <th className="px-3 py-2">{strings.table.name}</th>
            <th className="px-3 py-2 font-mono">{strings.table.type}</th>
            <th className="px-3 py-2 font-mono">{strings.table.credentials}</th>
          </tr>
        </thead>
        <tbody>
          {credentials.map((cred) => {
            const rowKey = `${cred.key}-${cred.clientId}`;
            const revealed = isRevealed(rowKey);
            const hasSecret = !!cred.clientSecret;
            const secretDisplay =
              !showSecret
                ? "—"
                : hasSecret
                  ? revealed
                    ? cred.clientSecret
                    : maskSecret(cred.clientSecret)
                  : "—";

            return (
              <tr key={rowKey} className="border-b border-border/40 last:border-0">
                <td className="px-3 py-2">
                  <Badge variant="outline" className="font-mono text-[10px]">
                    {cred.key}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-foreground">{cred.name}</td>
                <td className="px-3 py-2">
                  <Badge variant="accent" className="font-mono text-[10px] uppercase">
                    {cred.type}
                  </Badge>
                </td>
                <td className="px-3 py-2">
                  <div className="space-y-2">
                    <div className="flex items-start gap-1">
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          {strings.table.clientId}
                        </p>
                        <p className="break-all font-mono text-xs">{cred.clientId || "—"}</p>
                      </div>
                      <CopyButton value={cred.clientId} label="Client ID" />
                    </div>
                    {showSecret ? (
                      <div className="flex items-start gap-1">
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            {strings.table.secret}
                          </p>
                          <p
                            className={cn(
                              "break-all font-mono text-xs",
                              hasSecret && !revealed && "blur-[2px]",
                            )}
                          >
                            {secretDisplay}
                          </p>
                        </div>
                        {hasSecret ? (
                          <div className="flex shrink-0 items-center">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-7"
                              aria-label={revealed ? "Hide" : "Reveal"}
                              onClick={() => toggleReveal(rowKey)}
                            >
                              {revealed ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
                            </Button>
                            <CopyButton value={cred.clientSecret ?? ""} label="Secret" />
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


